import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { FAQConfigDTO, FAQItemDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid faq config': '常见问题配置无效，请检查列表内容',
      'invalid faq item': '常见问题条目无效，问题和答案均必填',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function normalizeItem(item: FAQItemDTO): FAQItemDTO {
  return {
    id: item.id?.trim() ?? '',
    market_id: item.market_id?.trim() || 'CN',
    question: item.question?.trim() ?? '',
    answer_html: item.answer_html?.trim() ?? '',
    images: [],
    sort_order: Number.isFinite(Number(item.sort_order)) ? Number(item.sort_order) : 0,
    enabled: Boolean(item.enabled),
  };
}

function createEmptyItem(index: number): FAQItemDTO {
  return {
    id: '',
    market_id: 'CN',
    question: '',
    answer_html: '',
    images: [],
    sort_order: (index + 1) * 10,
    enabled: true,
  };
}

function validateItems(items: FAQItemDTO[]): string | null {
  for (const [index, item] of items.entries()) {
    const number = index + 1;
    if (!item.question.trim()) {
      return `第 ${number} 条常见问题的问题不能为空`;
    }
    if (!item.answer_html.trim()) {
      return `第 ${number} 条常见问题的答案不能为空`;
    }
  }
  return null;
}

export function useFAQConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<FAQConfigDTO | null>(null);
  const [items, setItems] = useState<FAQItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getFAQConfig();
      setData(result);
      setItems((result.items ?? []).map(normalizeItem));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addItem = useCallback(() => {
    setItems((current) => [...current, createEmptyItem(current.length)]);
  }, []);

  const updateItem = useCallback((index: number, patch: Partial<FAQItemDTO>) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  }, []);

  const save = useCallback(async () => {
    const normalizedItems = items.map(normalizeItem);
    const validationError = validateItems(normalizedItems);
    if (validationError) {
      addToast('error', validationError);
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateFAQConfig({ items: normalizedItems });
      setData(result);
      setItems((result.items ?? []).map(normalizeItem));
      addToast('success', '常见问题配置已保存');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, items]);

  return {
    data,
    items,
    loading,
    saving,
    error,
    addItem,
    updateItem,
    removeItem,
    save,
    reload: load,
  };
}
